import React, { useState } from 'react';
import './PayPalPayment.css';

interface PayPalPaymentProps {
  amount: number;
  orderId?: string;
  onSuccess: (orderId: string) => void;
  onError: (error: string) => void;
  onCancel?: () => void;
}

const PayPalPayment: React.FC<PayPalPaymentProps> = ({ 
  amount, 
  orderId, 
  onSuccess, 
  onError,
  onCancel 
}) => {
  const [processing, setProcessing] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showLogin, setShowLogin] = useState(false);

  const handlePayPalClick = () => {
    setShowLogin(true);
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      onError('Bitte geben Sie Ihre PayPal-Anmeldedaten ein');
      return;
    }
    
    setProcessing(true);
    
    // Mock PayPal payment processing
    setTimeout(() => {
      // Simulate different test scenarios
      if (email === 'error@test.com') {
        onError('PayPal-Zahlung wurde abgelehnt');
        setProcessing(false);
        setShowLogin(false);
        return;
      }
      
      const mockOrderId = orderId || 'PAYPAL_' + Date.now();
      onSuccess(mockOrderId);
      setProcessing(false);
      setShowLogin(false);
    }, 2500);
  };

  const handleCancel = () => {
    setShowLogin(false);
    if (onCancel) onCancel();
  };

  if (showLogin) {
    return (
      <div className="paypal-login-container">
        <div className="paypal-header">
          <div className="paypal-logo">
            <span className="pay">Pay</span>
            <span className="pal">Pal</span>
          </div>
          <button className="close-button" onClick={handleCancel}>✕</button>
        </div>

        <div className="paypal-login-form">
          <h3>Mit PayPal einloggen</h3>
          <p className="amount-display">Zu zahlen: <strong>€{(amount / 100).toFixed(2)}</strong></p>
          
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <input
                type="email"
                placeholder="E-Mail oder Mobilnummer"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={processing}
                required
              />
            </div>
            
            <div className="form-group">
              <input
                type="password"
                placeholder="Passwort"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={processing}
                required
              />
            </div>

            <button 
              type="submit" 
              className="paypal-login-button"
              disabled={processing}
            >
              {processing ? (
                <span className="processing">
                  <span className="spinner"></span>
                  Verarbeitung...
                </span>
              ) : (
                'Einloggen'
              )}
            </button>
          </form>

          <div className="paypal-footer">
            <a href="#">Passwort vergessen?</a>
            <div className="separator">oder</div>
            <button className="create-account">Konto erstellen</button>
          </div>

          <div className="test-hint">
            <p>🧪 Demo-Modus: Beliebige E-Mail/Passwort</p>
            <p>❌ Fehler simulieren: error@test.com</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="paypal-payment-container">
      <div className="paypal-header">
        <h3>PayPal-Zahlung</h3>
        <div className="paypal-badges">
          <span className="badge">✓ Käuferschutz</span>
          <span className="badge">✓ Sicher</span>
        </div>
      </div>

      <div className="paypal-content">
        <p className="description">
          Schnell und sicher mit Ihrem PayPal-Konto bezahlen.
        </p>

        <div className="payment-amount">
          <span>Zu zahlender Betrag:</span>
          <span className="amount">€{(amount / 100).toFixed(2)}</span>
        </div>

        <button 
          className="paypal-button"
          onClick={handlePayPalClick}
          disabled={processing}
        >
          <div className="paypal-button-content">
            <span className="paypal-logo-inline">
              <span className="pay">Pay</span>
              <span className="pal">Pal</span>
            </span>
            <span className="button-text">Checkout</span>
          </div>
        </button>

        <div className="alternative-payment">
          <div className="divider">
            <span>oder zahlen Sie mit</span>
          </div>
          <button className="debit-button" disabled>
            💳 Lastschrift über PayPal
          </button>
        </div>

        <div className="paypal-benefits">
          <div className="benefit">
            <span className="icon">🛡️</span>
            <div>
              <strong>Käuferschutz</strong>
              <p>Ihre Einkäufe sind abgesichert</p>
            </div>
          </div>
          <div className="benefit">
            <span className="icon">🔒</span>
            <div>
              <strong>Datenschutz</strong>
              <p>Ihre Daten bleiben geschützt</p>
            </div>
          </div>
          <div className="benefit">
            <span className="icon">⚡</span>
            <div>
              <strong>Schnelle Zahlung</strong>
              <p>In wenigen Klicks bezahlen</p>
            </div>
          </div>
        </div>
      </div>

      <div className="payment-footer">
        <button className="cancel-link" onClick={onCancel}>
          ← Zurück zu anderen Zahlungsmethoden
        </button>
      </div>
    </div>
  );
};

export default PayPalPayment;
